import { connectWebSocket, send, api } from '../serverClient';

// Queue state is managed server-side via the WebSocket connection.
// No state to clear from the client. Kept as a no-op so sign-out
// paths can defensively call it without caring whether the queue
// is in a state that needs resetting.
export async function clearQueueState(_token) {
  return { ok: true };
}

// Leave any active matchmaking queue the player might still be in.
// Still reachable from sign-out and from the ArenaMatchmaking
// "Preparing match with X" screen's Cancel button.
export async function leaveQueue(_token) {
  await connectWebSocket();
  send('matchmaking:leave', {});
  return { ok: true };
}

// Match reward claiming moved to utils/arena/matchReporting.js
// (server-arbitrated outcome reports). The legacy claimMatchReward
// helper was removed; callers should use reportMatchOutcome instead.

// Purchase a single copy of a card with Arcana Shards. The client
// only declares which card; the server looks up the rarity, charges
// the correct price, and atomically deducts shards + adds to the
// collection. Returns { cardId, rarity, shardsSpent, newTotals } on
// success.
export async function purchaseCardWithShards({ cardId }) {
  return api.post('/profile/me/cards/purchase', { cardId });
}

// Disenchant N copies of an owned card back into Arcana Shards.
// Server-gated to players with the Inscription capstone. Returns
// { cardId, rarity, quantity, refundEach, shardsGained, newTotals }
// on success; 403 if the capstone isn't owned, 400 if the player
// doesn't have enough copies.
export async function disenchantCard({ cardId, artGroup, quantity }) {
  return api.post('/profile/me/cards/disenchant', {
    cardId, artGroup: artGroup || 'base', quantity,
  });
}

// Paginated. Returns `{ rows, total, offset, limit, mode }`. Filters
// are optional. Tier narrows to a single rank bracket (ranked mode
// only; ignored server-side for limited/sandbox), search is a
// case-insensitive substring match on player name. `mode` selects
// which leaderboard view to read: 'ranked' (default, ordered by
// tier/division/LP), 'limited' (draft + sealed, ordered by limited
// wins/losses), or 'sandbox'.
//
// Backwards-compat: older server builds return a flat array (the full
// top-100 with no filters / no envelope). When we detect that shape we
// fall back to client-side filtering + slicing so the new UI keeps
// working until the server is upgraded.
// Everyone online right now with their activity label. Powers the hub's
// Players tab. `{ rows }`; an older server without the route throws and
// the caller shows the empty state.
export async function getOnlinePlayers() {
  return api.get('/leaderboard/online');
}

export async function getLeaderboard({ offset = 0, limit = 50, tier = null, search = '', mode = 'ranked', online = false } = {}) {
  const params = new URLSearchParams();
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  if (tier) params.set('tier', tier);
  if (search) params.set('search', search);
  if (mode && mode !== 'ranked') params.set('mode', mode);
  if (online) params.set('online', '1');
  try {
    const result = await api.get(`/leaderboard?${params}`);
    if (Array.isArray(result)) {
      let filtered = result;
      if (tier) filtered = filtered.filter((p) => p.tier === tier);
      if (search) {
        const q = search.toLowerCase();
        filtered = filtered.filter((p) => (p.name || '').toLowerCase().includes(q));
      }
      return {
        rows: filtered.slice(offset, offset + limit),
        total: filtered.length,
        offset,
        limit,
        mode: 'ranked',
      };
    }
    return {
      rows: Array.isArray(result?.rows) ? result.rows : [],
      total: typeof result?.total === 'number' ? result.total : 0,
      offset,
      limit,
      mode: result?.mode || mode,
    };
  } catch (err) {
    console.error('[matchmakingApi] getLeaderboard failed:', err);
    return { rows: [], total: 0, offset, limit, mode };
  }
}

// Caller's absolute rank in the unfiltered ladder for the given mode.
// Returns `{ rank, total, mode }` where rank is 1-based, or
// `{ rank: null, total, mode }` for accounts that haven't claimed a
// name yet.
export async function getSelfLeaderboardRank({ mode = 'ranked' } = {}) {
  const params = new URLSearchParams();
  if (mode && mode !== 'ranked') params.set('mode', mode);
  const qs = params.toString();
  try {
    return await api.get(`/leaderboard/me${qs ? `?${qs}` : ''}`);
  } catch (err) {
    console.error('[matchmakingApi] getSelfLeaderboardRank failed:', err);
    return { rank: null, total: 0, mode };
  }
}
